import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Skeleton } from '../../components/ui/Skeleton.js';
import { PagePanel } from '../../components/ui/PagePanel.js';
import { describeError } from '../../lib/api/http.js';
import { getAgendaTrash, restoreAppointment, restoreReminder } from '../../lib/api/personal.js';
import { absoluteLabel } from '../../lib/agendaView.js';

export const AGENDA_TRASH_KEY = ['agenda-trash'] as const;

/** Lixeira da agenda: o que foi apagado fica guardado por 30 dias e volta com um toque. */
export function TrashSheet({
  open,
  onClose,
  onRestored,
}: {
  open: boolean;
  onClose: () => void;
  onRestored: (message: string) => void;
}) {
  const trashQ = useQuery({
    queryKey: AGENDA_TRASH_KEY,
    queryFn: getAgendaTrash,
    enabled: open,
    staleTime: 5_000,
  });
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState('');

  const trash = trashQ.data;
  const days = trash?.retentionDays ?? 30;
  const rows = [
    ...(trash?.appointments ?? []).map((item) => ({
      key: `a-${item.id}-${item.occurrenceStart ?? ''}`,
      title: item.title,
      line: `Compromisso · ${absoluteLabel(new Date(item.startsAt), item.allDay === true)}`,
      restore: () =>
        restoreAppointment(
          item.seriesId ?? item.id,
          item.seriesId ? (item.occurrenceStart ?? item.startsAt) : undefined,
        ),
    })),
    ...(trash?.reminders ?? []).map((item) => ({
      key: `r-${item.id}`,
      title: item.message,
      line: `Lembrete · ${absoluteLabel(new Date(item.remindAt), false)}`,
      restore: () => restoreReminder(item.id),
    })),
  ];

  async function restore(row: (typeof rows)[number]) {
    if (busy) return;
    setBusy(row.key);
    setError('');
    try {
      await row.restore();
      await trashQ.refetch();
      onRestored(`Voltou para a agenda: ${row.title}.`);
    } catch (failure) {
      setError(describeError(failure, 'Não consegui restaurar agora. Tente de novo.'));
    } finally {
      setBusy(null);
    }
  }

  return (
    <PagePanel open={open} kicker="Agenda" title="Lixeira" onClose={onClose}>
      {trashQ.isLoading ? (
        <Skeleton variant="rows" label="Abrindo a lixeira" />
      ) : trashQ.isError ? (
        <p className="stg-empty">Não consegui abrir a lixeira agora. Tente de novo em instantes.</p>
      ) : rows.length === 0 ? (
        <p className="stg-empty">
          Nada apagado. O que você apagar fica guardado aqui por {days} dias.
        </p>
      ) : (
        <div className="sk-cascade stg-list">
          <p className="agv-hint">O que você apaga fica guardado por {days} dias.</p>
          {error && (
            <p className="agv-error" role="alert">
              {error}
            </p>
          )}
          {rows.map((row) => (
            <div key={row.key} className="stg-row">
              <span className="stg-row-t">
                <b>{row.title}</b>
                <small>{row.line}</small>
              </span>
              <button
                className="agv-restore"
                onClick={() => void restore(row)}
                disabled={busy !== null}
              >
                {busy === row.key ? 'Voltando' : 'Restaurar'}
              </button>
            </div>
          ))}
        </div>
      )}
    </PagePanel>
  );
}
